import type React from 'react'
import { useEffect, useState } from 'react'
import type { FileForProcessing, IOrder, IPrimer, NegativeControl, Sample } from '../../../../Model/index.js'
import { tracingAxios } from '../../../../Repository/Sentry.js'
import { Environment } from '../../../../environment.js'
import { useGetSamplePoolSelector, useGetAuthenticatedUser, useGetOrderSelector } from 'Flux/Selector/index.ts'
import { useGetOpenRequests } from 'Flux/Selector/Order/GetOpenRequests/getOpenRequestsSelector.ts'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'
import Actions from '../../../../Flux/index.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import { useDispatch } from 'react-redux'
import type { OpUpdateSample } from '../../HomePage/Modals/CSVModalTypes.js'
import type { PipelineOverrides } from './PipelineOverridesWidget.js'
import { PipelineOverridesWidget } from './PipelineOverridesWidget.js'
import { ProcessingTypes } from '../../../../Generated/ProcessingType.js'
import type { OrderCreditCost } from '../../../../Generated/OrderCreditCost.ts'
import type { OrderSubmissionPreview } from '../../../../Generated/OrderSubmissionPreview.ts'
import type { SequencingPlatformPreview } from '../../../../Generated/SequencingPlatformPreview.ts'
import type { SequencingPlatformKey } from '../../../../Generated/SequencingPlatform.ts'
import { SubmitSequencingPlatform } from './SubmitSequencingPlatform.js'
import { isEarlyAccessSequencingPlatform, needsSequencingPlatformChoice } from '../../../../Util/sequencingPlatform.ts'

export interface SubmitOrderModalProps {
  primer: IPrimer | 'SKIPPED'
  order: IOrder
}

export const SubmitOrderModal: React.FC<SubmitOrderModalProps & BaseModalProps> = ({ isOpen, onClose, primer, order }) => {
  const [agreementChecked, setAgreementChecked] = useState<boolean>(false)
  const [termsChecked, setTermsChecked] = useState<boolean>(false)
  const [creditCost, setCreditCost] = useState<OrderCreditCost | null>(null)
  const [submissionPreview, setSubmissionPreview] = useState<OrderSubmissionPreview | null>(null)
  const [platformPreview, setPlatformPreview] = useState<SequencingPlatformPreview | null>(null)
  const [hasDuplicates, setHasDuplicates] = useState(false)

  const [minLength, setMinLength] = useState<number | undefined>(undefined)
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined)
  const [minLengthVaries, setMinLengthVaries] = useState<boolean>(false)
  const [touched, setTouched] = useState<{ min: boolean; max: boolean }>({ min: false, max: false })

  const parseNum = (v: string) => (v === '' ? undefined : Number.parseInt(v, 10) || 0)

  const samplePools = useGetSamplePoolSelector()
  const authenticatedUser = useGetAuthenticatedUser()
  const openRequests = useGetOpenRequests()

  // The modal's props are a snapshot taken when it opened; an override made here moves the stored order's
  // version, and the next override has to be sent at that one.
  const storedOrder = useGetOrderSelector()
  const liveOrder = storedOrder && storedOrder !== 'LOADING' && storedOrder.id === order.id ? storedOrder : order
  const overrideInFlight = openRequests.includes(orderSlice.actions.overrideSequencingPlatformRequest.type)
  const sampleCount =
    samplePools === 'LOADING' ? 0 : samplePools.filter((pool) => pool.orderId === order.id).reduce((sum, pool) => sum + (pool.samples?.length ?? 0), 0)

  const [pipelineOverrides, setPipelineOverrides] = useState<PipelineOverrides | null>(null)

  const minError = minLength === undefined ? 'Enter a minimum length' : minLength < 0 ? 'Min length must be ≥ 0' : undefined

  const maxError =
    maxLength === undefined
      ? 'Enter a maximum length'
      : minLength !== undefined && maxLength <= minLength
        ? 'Max length must be greater than min'
        : undefined

  const isValid = !minError && !maxError

  const dispatch = useDispatch()

  const updateEnabled =
    samplePools !== 'LOADING' &&
    samplePools.find(
      (pool) =>
        pool.samples.find(
          (sample) => sample.minBasePairLength !== minLength || (maxLength !== undefined && sample.maxBasePairLength !== maxLength)
        ) ||
        pool.negativeControls.find(
          (sample) => sample.minBasePairLength !== minLength || (maxLength !== undefined && sample.maxBasePairLength !== maxLength)
        )
    )

  useEffect(() => {
    if (samplePools !== 'LOADING') {
      const allSamples = [...samplePools.flatMap((x) => x.samples)]
      const sampleNameSet = new Set(allSamples.map((x) => x.customerSampleName))
      setHasDuplicates(allSamples.length != sampleNameSet.size)
      const allMinLengths = new Set(
        samplePools
          .flatMap((x) => [...x.samples, ...x.negativeControls])
          .map((x) => x.minBasePairLength)
          .filter((x) => x !== undefined)
      )
      const allMaxLengths = new Set(
        samplePools
          .flatMap((x) => [...x.samples, ...x.negativeControls])
          .map((x) => x.maxBasePairLength)
          .filter((x) => x !== undefined)
      )
      if (allMinLengths.size > 1) {
        setMinLengthVaries(true)
      } else if (allMinLengths.size === 1) {
        const [first] = allMinLengths
        setMinLength(first)
      }
      if (allMaxLengths.size === 1) {
        const [first] = allMaxLengths
        setMaxLength(first)
      }
    }
  }, [samplePools])

  useEffect(() => {
    if (!isOpen) return
    tracingAxios
      .get<OrderCreditCost>(`${Environment.API_URL}/api/orders/${order.id}/credit-cost`, {
        withCredentials: true
      })
      .then((res) => setCreditCost(res.data))
      .catch(console.error)
  }, [isOpen, order.id])

  // Refetch the "how many samples will be preprocessed" preview whenever the samples change (e.g. the
  // in-modal min/max Update bumps sample versions, flipping them back to needing reprocessing). Edits
  // are not optimistic — samplePools only changes after the server write — so this reflects the
  // persisted state the submit will actually act on.
  useEffect(() => {
    if (!isOpen) return
    tracingAxios
      .get<OrderSubmissionPreview>(`${Environment.API_URL}/api/orders/${order.id}/submission-preview`, {
        withCredentials: true
      })
      .then((res) => setSubmissionPreview(res.data))
      .catch(console.error)
  }, [isOpen, order.id, samplePools])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    tracingAxios
      .get<SequencingPlatformPreview>(`${Environment.API_URL}/api/orders/${order.id}/sequencing-platform`, {
        withCredentials: true
      })
      .then((res) => setPlatformPreview(res.data))
      .catch(console.error)
  }, [isOpen, order.id, liveOrder.version, samplePools])

  const effectivePlatform = platformPreview?.effectiveSequencingPlatform ?? null
  const [earlyAccessAcknowledged, setEarlyAccessAcknowledged] = useState(false)
  const [acknowledgedPlatform, setAcknowledgedPlatform] = useState(effectivePlatform)
  // Reset during render, not in an effect, so Confirm is never enabled for a render on a platform nobody acknowledged.
  if (acknowledgedPlatform !== effectivePlatform) {
    setAcknowledgedPlatform(effectivePlatform)
    setEarlyAccessAcknowledged(false)
  }
  const earlyAccessBlocksSubmit = isEarlyAccessSequencingPlatform(effectivePlatform) && !earlyAccessAcknowledged

  const handleOverride = (platform: SequencingPlatformKey | null) => {
    setEarlyAccessAcknowledged(false)
    dispatch(
      Actions[orderSlice.name].overrideSequencingPlatformRequest({
        orderId: order.id,
        version: liveOrder.version || '0',
        platform
      })
    )
  }

  const platformIssues = platformPreview?.issues ?? []

  const handleSubmit = async () => {
    if (!agreementChecked || !termsChecked) {
      alert('You need to accept both the Data Processing Agreement and the Terms & Conditions.')
      return
    }

    if (pipelineOverrides) {
      try {
        await tracingAxios.post(
          `${Environment.API_URL}/api/admin/orders/${order.id}/pipelineOverrides`,
          {
            use_vsearch_instead_of_swarm: pipelineOverrides.useVsearchInsteadOfSwarm ? 'true' : 'false',
            max_sequences_per_sample: pipelineOverrides.maxSequencesPerSample?.toString(),
            max_samples: pipelineOverrides.maxSamples?.toString(),
            consensus_calling_max_reads: pipelineOverrides.consensusCallingMaxReads?.toString()
          },
          { withCredentials: true }
        )
      } catch (e) {
        console.error('Failed to update pipeline overrides', e)
        alert('Failed to update pipeline overrides. Please try again.')
        return
      }
    }

    dispatch(
      Actions[orderSlice.name].submitOrderRequest({
        orderId: order.id
      })
    )
    onClose()
  }

  const sampleOrNCToBatch: (
    s: Sample | NegativeControl,
    minBasePairLength: number | undefined,
    maxBasePairLength: number | undefined
  ) => OpUpdateSample = (s, minBasePairLength, maxBasePairLength) => {
    return {
      op: 'UpdateSample',
      orderId: s.orderId!,
      poolId: s.poolId!,
      sample: {
        ...s,
        minBasePairLength: minBasePairLength !== undefined ? minBasePairLength : s.minBasePairLength,
        maxBasePairLength: maxBasePairLength !== undefined ? maxBasePairLength : s.maxBasePairLength
      } as FileForProcessing
    }
  }

  const handleBasePairLengthUpdate = () => {
    if (samplePools !== 'LOADING') {
      samplePools.forEach((pool) => {
        dispatch(
          Actions[orderSlice.name].batchUpdateSamples({
            orderId: order.id,
            poolId: pool.id,
            ops: [
              ...pool.samples.map((s) => sampleOrNCToBatch(s, minLength, maxLength)),
              ...pool.negativeControls.map((s) => sampleOrNCToBatch(s, minLength, maxLength))
            ]
          })
        )
      })
    }
  }

  if (!isOpen) return null

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} testId={'order-submit-modal'}>
      {creditCost && (
        <div className="modal modal-open">
          <div className="modal-box w-full max-w-2xl">
            <h3 className="text-lg font-bold">Submit Order to Processing</h3>
            {primer !== 'SKIPPED' && (
              <div className="text-green-600 font-bold pb-4 text-xxs xs:text-xs md:text-base">
                The provided primer sequences have been recognized by our primer database.
              </div>
            )}
            {primer === 'SKIPPED' && (
              <div className="text-xxs xs:text-xs md:text-base">
                <div className="text-green-600 font-bold">Primer validation has been skipped as your samples are already trimmed.</div>
                <div className="mt-2 rounded border border-amber-400 bg-amber-50 p-3 text-amber-800">
                  <div className="font-bold">Please read carefully before submitting</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    <li>
                      Since the samples have already been trimmed, primers are optional. No primers have been indicated for this order.
                      spcfy cannot determine if the primers fit the indicated amplicon{' '}
                      {order.targetTaxonomy ? ` (${order.targetTaxonomy})` : ''}. Carefully examine if the choosen amplicon is correct,
                      otherwise results might be empty or inaccurate and you will still be charged.
                    </li>
                    <li>
                      Without primers spcfy can only best-guess the length parameters used for quality filtering. Please review your results
                      closely after quality filtering. Consider providing a suitable min length and max length below.
                    </li>
                  </ul>
                </div>
              </div>
            )}
            {hasDuplicates && (
              <div className="text-red-600 font-bold pb-4 text-xxs xs:text-xs md:text-base">
                You have used a sample name more than once. This may lead to hard to decipher analysis results. Please consider using unique
                sample names.
              </div>
            )}
            {primer !== 'SKIPPED' && (
              <div className="max-w-fit text-xxs xs:text-xs md:text-base">
                <div className="flex justify-between">
                  <div className="flex-col">
                    <div className="text-green-600 font-bold">Primer Name:</div>
                    <div className="text-green-600 font-bold">Reference publication:</div>
                    <div className="text-green-600 font-bold">Length of amplicon (bp):</div>
                  </div>
                  <div className="flex-col mx-10">
                    <div className="text-green-600">
                      {primer.forwardName} / {primer.reverseName}
                    </div>
                    <div className="text-green-600">{primer.reference}</div>
                    <div className="text-green-600">{primer.basePairLength}</div>
                  </div>
                </div>
              </div>
            )}
            {(order.sampleConfiguration === 'TRIMMED_SAMPLES' ||
              order.sampleConfiguration === 'MERGED_AND_TRIMMED_SAMPLES' ||
              (order.processingType === ProcessingTypes.EDNA.key && primer !== 'SKIPPED')) && (
              <form
                className="flex flex-col mt-4 mb-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (isValid) handleBasePairLengthUpdate()
                }}
                noValidate
              >
                <div className="flex font-bold pb-2">
                  {order.processingType === ProcessingTypes.EDNA.key && primer !== 'SKIPPED'
                    ? 'Confirm amplicon length settings from primer'
                    : 'Edit amplicon min/max length'}
                </div>

                <div className="flex justify-between space-x-2">
                  {/* Min */}
                  <div className="flex flex-col grow">
                    <label className="input">
                      <input
                        type="number"
                        className={`grow ${touched.min && minError ? 'border border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500' : ''}`}
                        placeholder="Minlength"
                        value={minLength ?? ''}
                        onChange={(e) => setMinLength(parseNum(e.target.value))}
                        onBlur={() => setTouched((t) => ({ ...t, min: true }))}
                        aria-invalid={Boolean(touched.min && minError)}
                        aria-describedby="min-help"
                      />
                    </label>

                    <span id="min-help" className={`text-xs ${touched.min && minError ? 'text-red-600' : 'italic text-gray-500'}`}>
                      {touched.min && minError
                        ? minError
                        : (minLengthVaries && 'Min length differs between samples, but is set') ||
                          (minLength === undefined && 'Min length is not set, default will be deduced from amplicon')}
                    </span>
                  </div>

                  {/* Max */}
                  <div className="flex flex-col grow">
                    <label className="input">
                      <input
                        type="number"
                        className={`grow ${touched.max && maxError ? 'border border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500' : ''}`}
                        placeholder="Maxlength"
                        value={maxLength ?? ''}
                        min={minLength !== undefined ? minLength + 1 : undefined}
                        onChange={(e) => setMaxLength(parseNum(e.target.value))}
                        onBlur={() => setTouched((t) => ({ ...t, max: true }))}
                        aria-invalid={Boolean(touched.max && maxError)}
                        aria-describedby="max-help"
                      />
                    </label>

                    <span id="max-help" className={`text-xs ${touched.max && maxError ? 'text-red-600' : 'italic text-gray-500'}`}>
                      {touched.max && maxError ? maxError : 'Default is suitable for most cases'}
                    </span>
                  </div>

                  {/* Action */}
                  <TooltipTrigger
                    tooltip={'Ensure your values are correct, updating to incorrect min/max values will invalidate the analysis'}
                    disabled={!updateEnabled || !isValid}
                    disabledTooltip={!updateEnabled ? 'No samples need to be updated' : 'Fix validation errors first'}
                  >
                    <button type="submit" className="btn btn-actionable" disabled={!updateEnabled || !isValid}>
                      Update
                    </button>
                  </TooltipTrigger>
                </div>
              </form>
            )}
            {submissionPreview && (
              <div className="mt-4 text-xxs xs:text-xs md:text-base">
                {submissionPreview.toProcess > 0 ? (
                  <p>
                    <strong>{submissionPreview.toProcess}</strong> of {submissionPreview.total} sample
                    {submissionPreview.total === 1 ? '' : 's'} will be sent to preprocessing
                    {submissionPreview.alreadyProcessed > 0 &&
                      ` (${submissionPreview.alreadyProcessed} already processed and will be skipped)`}
                    .
                  </p>
                ) : (
                  <div className="rounded border border-amber-400 bg-amber-50 p-3 text-amber-800">
                    <div className="font-bold">No samples will be sent to preprocessing</div>
                    <div className="mt-1">
                      None of the {submissionPreview.total} sample{submissionPreview.total === 1 ? '' : 's'} need
                      (re)processing — they are already preprocessed and unchanged. Submitting will simply mark the order as
                      preprocessed so you can proceed to postprocessing. If you expected samples to be reprocessed, cancel and
                      check that your changes were saved.
                    </div>
                  </div>
                )}
              </div>
            )}
            {platformPreview && (
              <SubmitSequencingPlatform
                preview={platformPreview}
                order={liveOrder}
                sampleCount={sampleCount}
                overrideInFlight={overrideInFlight}
                onOverride={handleOverride}
                earlyAccessAcknowledged={earlyAccessAcknowledged}
                onEarlyAccessAcknowledgedChange={setEarlyAccessAcknowledged}
              />
            )}
            <div className="mt-4">
              <p>Preprocessing is free and does not use credits. Costs encur when postprocessing starts</p>
              {creditCost && (
                <>
                  <p className="mt-2">
                    After preprocessing, postprocessing will require <strong>{creditCost.creditCost} sample credits</strong>. Your current
                    balance: <strong>{creditCost.currentBalance} credits</strong>.
                  </p>
                </>
              )}
            </div>
            <div className="mt-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  data-testid={'accept-agreement'}
                  type="checkbox"
                  className="checkbox checkbox-primary"
                  checked={agreementChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAgreementChecked(e.target.checked)}
                />
                <span>I accept the Data Processing Agreement of spcfy GmbH.</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer mt-2">
                <input
                  data-testid={'accept-terms'}
                  type="checkbox"
                  className="checkbox checkbox-primary"
                  checked={termsChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTermsChecked(e.target.checked)}
                />
                <span>I accept the Terms & Conditions of spcfy GmbH.</span>
              </label>
            </div>
            {authenticatedUser?.showAdminUx && <PipelineOverridesWidget onOverridesChange={setPipelineOverrides} />}
            <div className="modal-action">
              <button
                data-testid="submit-modal-btn"
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={platformIssues.length > 0 || overrideInFlight || earlyAccessBlocksSubmit || needsSequencingPlatformChoice(platformPreview)}
              >
                Confirm
              </button>
              <button onClick={() => onClose()} className="btn btn-outline">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </BaseModal>
  )
}
